import { useEffect, useMemo, useState } from "react";
import SourceHealthBadge from "../SourceHealthBadge";
import AdminBtn from "../layout/AdminBtn";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import {
  formatCompletedTime,
  formatRelativeTime,
} from "../../intelligence/intelligenceLabels";
import { findDataSourceInCatalog } from "./buildDataSourceCatalog";
import {
  canScheduleDataSource,
  canStopDataSource,
  canToggleDataSource,
  fetchDataSourceRuns,
  runDataSourceNow,
  saveDataSourceSchedule,
  stopDataSourceRun,
  toggleDataSourceEnabled,
} from "./dataSourceDetailActions";
import RunningElapsedLabel from "./RunningElapsedLabel";
import {
  resolveRunNowUnavailableReason,
  resolveScheduleUnavailableReason,
} from "./dataSourceActionAvailability";
import { collectionSummary, lastResultSummary } from "./dataSourceManageCopy";
import { buildSourceStatusDiagnosis } from "./dataSourceStatusDiagnosis";
import { DATA_SOURCE_SCHEDULE_INTERVALS } from "./dataSourceScheduleUtils";
import EditDataSourceModal from "./EditDataSourceModal";
import DeleteDataSourceModal from "./DeleteDataSourceModal";
import { navigateToDataSourceList } from "../../../../utils/dataSourceDetailRoutes";
import { fetchRealEstateRunById } from "../../../../services/realEstateSourceControls";

function scrollToDetailSection(id) {
  if (typeof document === "undefined") return;
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function formatDuration(ms) {
  if (ms == null || Number.isNaN(Number(ms))) return "—";
  const value = Number(ms);
  if (value < 1000) return `${value}ms`;
  const seconds = Math.round(value / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

function failedStatus(status) {
  const key = String(status || "").toLowerCase();
  return key === "failed" || key === "error" || key === "failing";
}

function DataSourceDetailNav({ currentLabel = null, domainLabel = null }) {
  return (
    <div className="admin-ds-detail__nav">
      <button type="button" className="admin-ds-detail__back-btn" onClick={navigateToDataSourceList}>
        ← Back
      </button>
      <nav className="admin-ds-detail__crumb" aria-label="Breadcrumb">
        <span>Data &amp; Ingestion</span>
        <span className="admin-ds-detail__crumb-sep" aria-hidden="true">
          /
        </span>
        <button type="button" className="admin-ds-detail__back" onClick={navigateToDataSourceList}>
          Data Sources
        </button>
        {domainLabel ? (
          <>
            <span className="admin-ds-detail__crumb-sep" aria-hidden="true">
              /
            </span>
            <span>{domainLabel}</span>
          </>
        ) : null}
        {currentLabel ? (
          <>
            <span className="admin-ds-detail__crumb-sep" aria-hidden="true">
              /
            </span>
            <span aria-current="page">{currentLabel}</span>
          </>
        ) : null}
      </nav>
    </div>
  );
}

function DetailStat({ label, children, alert = false }) {
  return (
    <div className={`admin-source-controls-dash__stat${alert ? " admin-source-controls-dash__stat--alert" : ""}`}>
      <span className="admin-source-controls-dash__stat-label">{label}</span>
      <span className="admin-source-controls-dash__stat-value">{children}</span>
    </div>
  );
}

export default function DataSourceDetailPage({
  domainId,
  sourceKey,
  catalog,
  isLoading,
  onRefresh,
  showToast,
  canManage,
  toast,
}) {
  const source = useMemo(
    () => findDataSourceInCatalog(catalog, domainId, sourceKey),
    [catalog, domainId, sourceKey]
  );
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState("");
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageSaving, setManageSaving] = useState(false);
  const [manageSaveError, setManageSaveError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isRunning =
    source?.health === "running" ||
    source?.health === "queued" ||
    Boolean(source?.isRunning) ||
    Boolean(source?.activeRunId) ||
    ["running", "queued", "started"].includes(
      String(
        source?.latestRun?.status || source?.lastStatus || source?.lastRunStatus || ""
      ).toLowerCase()
    );
  const runBlockedReason = resolveRunNowUnavailableReason(source, {
    canManageSources: canManage,
    isBusy: busy,
    isRunning,
  });
  const scheduleBlockedReason = resolveScheduleUnavailableReason(source, {
    canManageSources: canManage,
    isBusy: busy,
  });
  const canEditEnabled = canManage && canToggleDataSource(source);
  const canEditSchedule = canManage && canScheduleDataSource(source) && !scheduleBlockedReason;
  const canStop = canManage && canStopDataSource(source);

  useEffect(() => {
    if (!source) {
      setRuns([]);
      setRunsError("");
      return undefined;
    }

    let cancelled = false;
    const loadRuns = async () => {
      setRunsLoading(true);
      setRunsError("");
      try {
        const nextRuns = await fetchDataSourceRuns(source, { limit: 25 });
        if (!cancelled) {
          setRuns(Array.isArray(nextRuns) ? nextRuns : []);
        }
      } catch (error) {
        if (!cancelled) {
          setRuns([]);
          setRunsError(error.message || "Unable to load run history.");
        }
      } finally {
        if (!cancelled) {
          setRunsLoading(false);
        }
      }
    };

    loadRuns();
    return () => {
      cancelled = true;
    };
  }, [source]);

  useEffect(() => {
    if (!selectedRun?.id || source?.domainId !== "real_estate") {
      setRunDetail(null);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchRealEstateRunById(selectedRun.id);
        if (!cancelled) {
          setRunDetail(data.run || null);
        }
      } catch {
        if (!cancelled) {
          setRunDetail(null);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedRun, source?.domainId]);

  const failedRuns = useMemo(
    () =>
      (runs || []).filter((run) => failedStatus(run.status) || run.errorMessage || run.errorCount > 0),
    [runs]
  );
  const latestFailed = failedRuns[0] || null;
  // Keep the header "Last result" in sync with the first Recent runs row once history loads.
  const lastResultSource = useMemo(() => {
    if (!source) return null;
    const latest = runs?.[0];
    if (!latest) return source;
    return {
      ...source,
      lastRunAt: latest.startedAt || source.lastRunAt,
      lastStatus: latest.status || source.lastStatus,
      latestRun: {
        ...(source.latestRun || {}),
        ...latest,
        id: latest.id,
        runId: latest.id,
        status: latest.status,
        startedAt: latest.startedAt,
        finishedAt: latest.finishedAt,
        recordsFound: latest.recordsFound,
        recordsReceived: latest.recordsFound,
        recordsCreated: latest.recordsCreated,
        recordsInserted: latest.recordsCreated,
        recordsUpdated: latest.recordsUpdated,
      },
      recordsFound: latest.recordsFound,
      recordsCreated: latest.recordsCreated,
      recordsUpdated: latest.recordsUpdated,
    };
  }, [runs, source]);
  const errorMessages = [
    source?.lastErrorMessage,
    latestFailed?.errorMessage,
    ...(runDetail?.errors || []).map((item) => item.message || String(item)),
  ].filter(Boolean);
  const uniqueErrors = [...new Set(errorMessages)];

  const handleRunNow = async () => {
    if (!source || runBlockedReason) return;
    setBusy(true);
    try {
      const result = await runDataSourceNow(source);
      showToast?.(result?.message || `Collection started for ${source.displayName}.`, "success");
      await onRefresh?.({ silent: true });
    } catch (error) {
      showToast?.(error.message || `Unable to start collection for ${source.displayName}.`, "error");
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    if (!source || !canStop) return;
    setBusy(true);
    try {
      const result = await stopDataSourceRun(source);
      showToast?.(result?.message || `Stop requested for ${source.displayName}.`, "success");
      await onRefresh?.({ silent: true });
    } catch (error) {
      showToast?.(error.message || `Unable to stop ${source.displayName}.`, "error");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveManage = async (draft) => {
    if (!source) return;
    setManageSaving(true);
    setManageSaveError("");
    const nextSource = { ...source, enabled: draft.enabled };
    try {
      if (draft.enabledChanged && canEditEnabled) {
        await toggleDataSourceEnabled(source, draft.enabled);
      }
      if ((draft.scheduleChanged || draft.schedulePayload?.runImmediately) && canEditSchedule) {
        await saveDataSourceSchedule(nextSource, draft.schedulePayload);
      }
      setManageOpen(false);
      setManageSaveError("");
      setManageSaving(false);
      showToast?.(`Saved changes for ${source.displayName}.`, "success");
      void onRefresh?.({ silent: true });
      const nextRunBlocked = resolveRunNowUnavailableReason(nextSource, {
        canManageSources: canManage,
        isBusy: false,
        isRunning,
      });
      // Fire-and-forget — do not keep Manage in a saving state while collection starts.
      if (draft.runImmediately && !nextRunBlocked && draft.enabled) {
        void (async () => {
          setBusy(true);
          try {
            const result = await runDataSourceNow(nextSource);
            showToast?.(
              result?.message || `Collection started for ${source.displayName}.`,
              "success"
            );
            await onRefresh?.({ silent: true });
          } catch (error) {
            showToast?.(
              error.message || `Unable to start collection for ${source.displayName}.`,
              "error"
            );
          } finally {
            setBusy(false);
          }
        })();
      }
    } catch (error) {
      const message = error.message || "Unable to save source changes.";
      setManageSaveError(message);
      showToast?.(message, "error");
      setManageSaving(false);
    }
  };

  if (isLoading && !source) {
    return <AdminStatePanel variant="loading" message="Loading data source…" />;
  }

  if (!source) {
    return (
      <div className="admin-dashboard admin-source-controls-dash">
        <header className="admin-dashboard__header admin-source-controls-dash__header">
          <div className="admin-source-controls-dash__header-copy">
            <DataSourceDetailNav />
            <h1 className="admin-dashboard__title">Data source</h1>
            <p className="admin-source-controls-dash__subtitle">
              This source could not be found in the current catalog.
            </p>
          </div>
        </header>
        <AdminStatePanel
          variant="error"
          message={`No source matched ${domainId}/${sourceKey}. It may have been deleted, or it belongs to a domain that failed to load.`}
          onRetry={() => onRefresh?.()}
        />
      </div>
    );
  }

  const health = source.health || "unknown";
  const sourceUrl = source.sourceUrl || source.providerUrl || source.listingUrl || null;
  const diagnosis = buildSourceStatusDiagnosis(source, {
    health,
    lastResultSource,
    errors: uniqueErrors,
    failedRunCount: failedRuns.length,
    runBlockedReason,
    scheduleBlockedReason,
    canStop,
    isRunning,
    canManage,
  });

  const handleDiagnosisAction = (actionId) => {
    switch (actionId) {
      case "run_now":
        void handleRunNow();
        break;
      case "stop":
        void handleStop();
        break;
      case "open_manage":
        setManageOpen(true);
        break;
      case "review_errors":
        scrollToDetailSection("ds-detail-errors");
        break;
      case "review_runs":
        scrollToDetailSection("ds-detail-runs");
        break;
      case "refresh":
        void onRefresh?.();
        break;
      default:
        break;
    }
  };

  return (
    <div className="admin-dashboard admin-source-controls-dash admin-ds-detail">
      {toast ? (
        <div className={`admin-re-toast admin-re-toast--${toast.tone}`} role="status" aria-live="polite">
          {toast.message}
        </div>
      ) : null}

      <header className="admin-dashboard__header admin-source-controls-dash__header">
        <div className="admin-source-controls-dash__header-copy">
          <DataSourceDetailNav
            domainLabel={source.domainLabel || domainId}
            currentLabel={source.displayName}
          />
          <h1 className="admin-dashboard__title">{source.displayName}</h1>
          <p className="admin-source-controls-dash__subtitle">
            <code>{source.sourceKey}</code>
            {source.extraSourceLines?.length ? ` · ${source.extraSourceLines.join(" · ")}` : ""}
          </p>
        </div>
        <div className="admin-dashboard__header-actions admin-source-controls-dash__header-actions">
          <AdminBtn variant="secondary" onClick={() => onRefresh?.()} disabled={isLoading || busy}>
            Refresh
          </AdminBtn>
          {canStop ? (
            <AdminBtn
              variant="danger"
              onClick={handleStop}
              disabled={busy}
              title="Stop the in-progress sync at the next safe checkpoint"
            >
              {busy ? "Stopping…" : "Stop"}
            </AdminBtn>
          ) : (
            <AdminBtn
              variant="primary"
              onClick={handleRunNow}
              disabled={Boolean(runBlockedReason) || busy}
              title={runBlockedReason || "Start a collection now"}
            >
              {busy || isRunning ? "Running…" : "Run now"}
            </AdminBtn>
          )}
          <AdminBtn variant="secondary" onClick={() => setManageOpen(true)}>
            {canManage ? "Manage" : "View settings"}
          </AdminBtn>
        </div>
      </header>

      <section className="admin-source-controls-dash__stats" aria-label="Source status">
        <DetailStat label="Current state">
          <div className="admin-ds-status-with-elapsed">
            <SourceHealthBadge status={health} />
            <RunningElapsedLabel source={source} isRunning={isRunning} />
          </div>
        </DetailStat>
        <DetailStat label="Collection">{collectionSummary(source)}</DetailStat>
        <DetailStat label="Last result">{lastResultSummary(lastResultSource)}</DetailStat>
        <DetailStat label="Next run">
          {source.nextRunAt ? formatCompletedTime(source.nextRunAt) : "Not scheduled"}
        </DetailStat>
        <DetailStat label="Last failure" alert={Boolean(source.lastErrorMessage || latestFailed)}>
          {source.lastFailureAt
            ? formatRelativeTime(source.lastFailureAt) || formatCompletedTime(source.lastFailureAt)
            : latestFailed?.startedAt
              ? formatRelativeTime(latestFailed.startedAt) || formatCompletedTime(latestFailed.startedAt)
              : source.lastErrorMessage
                ? "See errors"
                : "None recorded"}
        </DetailStat>
      </section>

      <div className="admin-ds-detail__body">
        <section
          className={`admin-ds-detail__card admin-ds-detail__card--status admin-ds-detail__card--status-${diagnosis.tone}`}
          aria-labelledby="ds-detail-status-heading"
        >
          <div className="admin-ds-detail__status-head">
            <div>
              <h2 id="ds-detail-status-heading">Current state</h2>
              <div className="admin-ds-detail__status-badge-row">
                <SourceHealthBadge status={health} />
                <RunningElapsedLabel source={source} isRunning={isRunning} />
              </div>
            </div>
          </div>

          <p className="admin-ds-detail__status-summary">{diagnosis.summary}</p>

          {diagnosis.evidence.length ? (
            <div className="admin-ds-detail__status-block">
              <h3>Why this state</h3>
              <ul>
                {diagnosis.evidence.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {diagnosis.attention.length ? (
            <div className="admin-ds-detail__status-block">
              <h3>Pay attention to</h3>
              <ul>
                {diagnosis.attention.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {diagnosis.actions.length ? (
            <div className="admin-ds-detail__status-actions" aria-label="Suggested actions">
              {diagnosis.actions.map((action) => {
                const disabled =
                  busy ||
                  (action.id === "run_now" && Boolean(runBlockedReason)) ||
                  (action.id === "stop" && !canStop);
                const title =
                  action.id === "run_now" && runBlockedReason
                    ? runBlockedReason
                    : action.label;
                return (
                  <AdminBtn
                    key={action.id}
                    variant={
                      action.danger ? "danger" : action.primary ? "primary" : "secondary"
                    }
                    onClick={() => handleDiagnosisAction(action.id)}
                    disabled={disabled}
                    title={title}
                  >
                    {action.label}
                  </AdminBtn>
                );
              })}
            </div>
          ) : null}

          <dl className="admin-ds-detail__meta">
            <div>
              <dt>Enabled</dt>
              <dd>{source.enabled ? "Yes" : "No"}</dd>
            </div>
            <div>
              <dt>Last success</dt>
              <dd>{source.lastSuccessAt ? formatCompletedTime(source.lastSuccessAt) : "—"}</dd>
            </div>
            <div>
              <dt>Last run</dt>
              <dd>
                {lastResultSource?.lastRunAt
                  ? formatCompletedTime(lastResultSource.lastRunAt)
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>Records last seen</dt>
              <dd>
                Found {lastResultSource?.recordsFound ?? 0}, added{" "}
                {lastResultSource?.recordsCreated ?? 0}, updated{" "}
                {lastResultSource?.recordsUpdated ?? 0}
              </dd>
            </div>
            {sourceUrl ? (
              <div className="admin-ds-detail__meta-full">
                <dt>Source URL</dt>
                <dd>
                  <a href={sourceUrl} target="_blank" rel="noreferrer">
                    {sourceUrl}
                  </a>
                </dd>
              </div>
            ) : null}
          </dl>
          {runBlockedReason ? <p className="admin-ds-detail__hint">{runBlockedReason}</p> : null}
          {scheduleBlockedReason ? <p className="admin-ds-detail__hint">{scheduleBlockedReason}</p> : null}
        </section>

        <section
          id="ds-detail-errors"
          className={`admin-ds-detail__card${uniqueErrors.length ? " admin-ds-detail__card--error" : ""}`}
        >
          <h2>Errors</h2>
          {uniqueErrors.length ? (
            <ul className="admin-ds-detail__errors">
              {uniqueErrors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          ) : (
            <p className="muted">No errors are recorded for this source.</p>
          )}
          {failedRuns.length ? (
            <p className="admin-ds-detail__hint">
              {failedRuns.length} recent run{failedRuns.length === 1 ? "" : "s"} finished with an
              error. Use Recent runs to compare whether this is repeating.
            </p>
          ) : null}
        </section>

        <section id="ds-detail-runs" className="admin-ds-detail__card admin-ds-detail__card--wide">
          <div className="admin-ds-detail__card-head">
            <h2>Recent runs</h2>
            <p className="admin-ds-detail__card-lead">
              Compare status and found/added/updated across attempts. Click a status badge for run
              detail when available.
            </p>
          </div>
          {runsLoading ? <p className="muted">Loading run history…</p> : null}
          {runsError ? <p className="admin-data-health-error">{runsError}</p> : null}
          {!runsLoading && !runsError && !runs?.length ? (
            <p className="muted">No ingestion runs recorded yet.</p>
          ) : null}
          {runs?.length ? (
            <AdminTableContainer>
              <table className="admin-data-health-table admin-re-controls-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Found</th>
                    <th>Added</th>
                    <th>Updated</th>
                    <th>Trigger</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr
                      key={run.id || `${run.startedAt}-${run.status}`}
                      className={selectedRun?.id === run.id ? "admin-ds-detail__run--selected" : ""}
                    >
                      <td>
                        <button
                          type="button"
                          className="admin-ds-source-name-link"
                          onClick={() => setSelectedRun(run)}
                        >
                          <SourceHealthBadge status={String(run.status || "unknown").toLowerCase()} />
                        </button>
                      </td>
                      <td>{formatCompletedTime(run.startedAt)}</td>
                      <td>{formatDuration(run.durationMs)}</td>
                      <td>{run.recordsFound}</td>
                      <td>{run.recordsCreated}</td>
                      <td>{run.recordsUpdated}</td>
                      <td>{run.triggeredBy || "—"}</td>
                      <td className="admin-re-error-cell" title={run.errorMessage || ""}>
                        {run.errorMessage
                          ? run.errorMessage.length > 80
                            ? `${run.errorMessage.slice(0, 80)}…`
                            : run.errorMessage
                          : run.errorCount > 0
                            ? run.errorCount
                            : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableContainer>
          ) : null}
          {runDetail ? (
            <div className="admin-ds-detail__run-detail">
              <h3>Selected run</h3>
              <p className="muted">
                {runDetail.runId} · {runDetail.triggeredBy || "unknown trigger"} · errors{" "}
                {runDetail.errorCount ?? 0}, warnings {runDetail.warningCount ?? 0}
              </p>
              {runDetail.errors?.length ? (
                <ul className="admin-ds-detail__errors">
                  {runDetail.errors.map((item, index) => (
                    <li key={`${item.message}-${index}`}>{item.message || String(item)}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>

      {manageOpen ? (
        <EditDataSourceModal
          source={source}
          canManage={canManage}
          isSaving={manageSaving}
          intervals={DATA_SOURCE_SCHEDULE_INTERVALS}
          canEditEnabled={canEditEnabled}
          canEditSchedule={canEditSchedule}
          scheduleBlockedReason={scheduleBlockedReason}
          runBlockedReason={runBlockedReason}
          saveError={manageSaveError}
          canStop={canStop}
          stopBusy={busy}
          onClose={() => {
            if (manageSaving) return;
            setManageOpen(false);
            setManageSaveError("");
          }}
          onSave={handleSaveManage}
          onRunNow={
            runBlockedReason || canStop
              ? null
              : () => {
                  setManageOpen(false);
                  setManageSaveError("");
                  handleRunNow();
                }
          }
          onStop={canStop ? () => handleStop() : null}
          onDelete={
            canManage
              ? () => {
                  setManageOpen(false);
                  setManageSaveError("");
                  setDeleteOpen(true);
                }
              : null
          }
        />
      ) : null}

      {deleteOpen ? (
        <DeleteDataSourceModal
          source={source}
          domain={source.domainId}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => {
            showToast?.(`${source.displayName} deleted.`, "success");
            navigateToDataSourceList();
          }}
        />
      ) : null}
    </div>
  );
}
